import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
  CardQuestionsGrid,
  DownloadButton,
  SummaryContainer,
  Header,
  SummaryTitle,
  Body,
  pdfStylesToOverride,
  SummaryButtonDiv,
  SummaryButton,
  ExclamationMark,
} from './styles';
import ListStyleQuestionSummary from '../../../components/SummaryQuestions/ListStyleQuestions';
import OpenStyleQuestionSummary from '../../../components/SummaryQuestions/OpenStyleQuestions';
import CardStyleQuestionSummary from '../../../components/SummaryQuestions/CardStyleQuestions';
import CheckBoxStyleQuestionSummary from '../../../components/SummaryQuestions/CheckBoxStyleQuestions';
import api from '../../../services/api';
import EnumSurveySummaryType from '../../../utils/enums/EnumSurveySummaryType';

interface Props {
  survey: any;
  results: any;
}

const LEAVE_SURVEY_LINK_TEXT = 'Klicken Sie hier, um die Umfrage zu verlassen';

const DEFAULT_SUMMARY_BUTTON_FONT_SIZE = '30px';

const getLeaveSurveyLinkFontSize = (completedHtml?: string): string => {
  if (!completedHtml) return DEFAULT_SUMMARY_BUTTON_FONT_SIZE;

  const normalize = (text: string | null): string =>
    (text ?? '').replace(/\s+/g, ' ').trim();

  const parser = new DOMParser();
  const doc = parser.parseFromString(completedHtml, 'text/html');

  const linkElement = Array.from(
    doc.querySelectorAll('a, span, p, div, font'),
  ).find(el => normalize(el.textContent) === LEAVE_SURVEY_LINK_TEXT);

  if (!linkElement) return DEFAULT_SUMMARY_BUTTON_FONT_SIZE;

  let current: HTMLElement | null = linkElement as HTMLElement;
  while (current) {
    if (current.style?.fontSize) {
      return current.style.fontSize;
    }
    current = current.parentElement;
  }

  return DEFAULT_SUMMARY_BUTTON_FONT_SIZE;
};

const SurveySummary: React.FC<Props> = ({ survey, results }) => {
  const [cardStyleList, setCardStyleList] = useState([]);
  const [otherQuestionsList, setOtherQuestionsList] = useState([]);
  const [showSummaryButton, setShowSummaryButton] = useState<boolean>(false);
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
  const summaryRef = React.useRef<HTMLDivElement>(null);
  const prefetchedPdfRef = React.useRef<Promise<Blob> | null>(null);

  const summaryButtonFontSize = React.useMemo(
    () => getLeaveSurveyLinkFontSize(survey?.completedHtml),
    [survey?.completedHtml],
  );

  useEffect(() => {
    if (survey.idSurvey) {
      api
        .get(`/surveySummary/survey/${survey.idSurvey}`)
        .then(response => {
          if (response.data.length > 0) {
            setShowSummaryButton(true);

            const summaryWithResults = response.data.map(
              (summaryQuestion: any) => {
                const matchingResult = results.questionResults.find(
                  (result: any) =>
                    result.questionId === summaryQuestion.questionId,
                );

                return {
                  ...summaryQuestion,
                  result: matchingResult ?? [],
                };
              },
            );

            setCardStyleList(
              summaryWithResults.filter(
                (summaryQuestion: any) =>
                  summaryQuestion.type === EnumSurveySummaryType.MainCard,
              ),
            );
            setOtherQuestionsList(
              summaryWithResults.filter(
                (summaryQuestion: any) =>
                  summaryQuestion.type !== EnumSurveySummaryType.MainCard,
              ),
            );
          }
        })
        .catch(error => {
          console.error('Error fetching summary data:', error);
        });
    }
  }, [results.questionResults, survey.idSurvey]);

  const getStylesForPdf = (): string => {
    const filterMediaRules = (styleEl: HTMLStyleElement): string => {
      const sheet = styleEl.sheet as CSSStyleSheet | null;
      if (!sheet) return styleEl.outerHTML;

      const rules = Array.from(sheet.cssRules)
        .filter(rule => !(rule instanceof CSSMediaRule))
        .map(rule => rule.cssText)
        .join('\n');

      return `<style>${rules}</style>`;
    };

    const styles =
      Array.from(
        document.querySelectorAll<HTMLStyleElement>('style[data-styled]'),
      )
        .map(filterMediaRules)
        .join('') +
      Array.from(
        document.querySelectorAll<HTMLStyleElement>('style:not([data-styled])'),
      )
        .map(filterMediaRules)
        .join('') +
      Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map(l => l.outerHTML)
        .join('');

    return styles;
  };

  const requestPdfBlob = useCallback(async (): Promise<Blob> => {
    const summaryContent = summaryRef.current?.outerHTML ?? '';

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          ${getStylesForPdf()}
          <style>
            ${pdfStylesToOverride}
          </style>
        </head>
        <body>
          ${summaryContent}
        </body>
      </html>
    `;

    const response = await api.post(
      `/surveySummary/getPdfSummary`,
      { html, projectId: survey.projectId, surveyId: survey.idSurvey },
      {
        responseType: 'blob',
      },
    );

    return new Blob([response.data], {
      type: 'application/pdf',
    });
  }, [survey.idSurvey, survey.projectId]);

  // Kicks off PDF generation as soon as the summary is shown, so the
  // multi-second backend work overlaps with the user reading the summary
  // instead of happening only after they click Download.
  useEffect(() => {
    if (showSummary) {
      const prefetchPromise = requestPdfBlob();
      // Attach a no-op catch so a failed prefetch that's never awaited
      // (user never clicks Download) doesn't surface as an unhandled
      // rejection; the real error is still handled where it's awaited.
      prefetchPromise.catch(() => {});
      prefetchedPdfRef.current = prefetchPromise;
    }
  }, [showSummary, requestPdfBlob]);

  const getPdfSummary = useCallback(async () => {
    setIsDownloadingPdf(true);

    try {
      const blob = prefetchedPdfRef.current
        ? await prefetchedPdfRef.current.catch(() => requestPdfBlob())
        : await requestPdfBlob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'survey-summary.pdf';

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [requestPdfBlob]);

  const handleShowSummary = useCallback(() => {
    // removing the completed survey message
    const container = document.querySelector('.sv-container-modern');
    if (container) {
      container.parentElement?.removeChild(container);
    }

    setShowSummary(true);
    setShowSummaryButton(false);
  }, []);

  if (showSummaryButton) {
    return (
      <SummaryButtonDiv>
        <SummaryButton
          $fontSize={summaryButtonFontSize}
          onClick={handleShowSummary}
        >
          <ExclamationMark>!</ExclamationMark> Klicken Sie hier, um einen Auszug
          Ihrer Antworten zu sehen. <ExclamationMark>!</ExclamationMark>
        </SummaryButton>
      </SummaryButtonDiv>
    );
  }

  if (showSummary) {
    return (
      <>
        {survey.idSurvey ? (
          <SummaryContainer>
            <Header>
              <SummaryTitle>Auszug aus Ihren Antworten</SummaryTitle>
              <DownloadButton
                variant="contained"
                onClick={getPdfSummary}
                disabled={isDownloadingPdf}
              >
                {isDownloadingPdf ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Download'
                )}
              </DownloadButton>
            </Header>

            <div
              style={{
                width: '100%',
                height: '100%',
              }}
              ref={summaryRef}
            >
              <Body>
                <CardQuestionsGrid id="card-questions-grid">
                  {cardStyleList.map((question: any, index: number) => (
                    <CardStyleQuestionSummary
                      key={index}
                      cardQuestion={question}
                    />
                  ))}
                </CardQuestionsGrid>

                {otherQuestionsList.map((question: any, index: number) => {
                  if (question.type === EnumSurveySummaryType.ListItem) {
                    return (
                      <ListStyleQuestionSummary
                        key={index}
                        listQuestion={question}
                      />
                    );
                  }

                  if (question.type === EnumSurveySummaryType.CheckBox) {
                    return (
                      <CheckBoxStyleQuestionSummary
                        key={index}
                        checkBoxQuestion={question}
                      />
                    );
                  }

                  return (
                    <OpenStyleQuestionSummary
                      key={index}
                      openQuestion={question}
                    />
                  );
                })}
              </Body>
            </div>
          </SummaryContainer>
        ) : (
          <div>There is no summary to display.</div>
        )}
      </>
    );
  }

  return <></>;
};

export default SurveySummary;
